// eslint-disable-next-line import/no-extraneous-dependencies
import { Doughnut } from 'react-chartjs-2';
import { Box, Paper, Skeleton, Typography } from '@mui/material';

import { chartColors, formatCurrencyToMil } from '~/shared/utils/charts';
import { DashCampaignsModelView } from '~/types';

interface DonutChartProps {
  data: DashCampaignsModelView;
  isLoading: boolean;
}

export function DonutChart({ data, isLoading }: DonutChartProps) {
  if (isLoading) {
    return (
      <Paper sx={{ flex: '1 1 300px' }}>
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            padding: '24px',
            height: '340px',
          }}
        >
          <Skeleton
            variant="text"
            sx={{ fontSize: '1.25rem', mb: 2, width: '200px' }}
          />
          <Skeleton
            variant="rectangular"
            sx={{
              height: '280px',
              borderRadius: '4px',
            }}
          />
        </Box>
      </Paper>
    );
  }

  const donutData = {
    labels: (() => {
      try {
        const items = data?.segments?.items ?? [];
        const sortedItems = [...items].sort(
          (a, b) => (b?.percentage ?? 0) - (a?.percentage ?? 0)
        );

        const top5 = sortedItems.slice(0, 5);
        const labels = top5.map(
          item =>
            `${item?.segmentsName ?? 'Sem nome'} ${(item?.percentage ?? 0).toFixed(1)}%`
        );

        if (sortedItems.length > 5) {
          const othersPercentage = sortedItems
            .slice(5)
            .reduce((sum, item) => sum + (item?.percentage ?? 0), 0);
          labels.push(`Outros ${othersPercentage.toFixed(1)}%`);
        }

        return labels;
      } catch (error) {
        return ['Sem dados'];
      }
    })(),
    datasets: [
      {
        data: (() => {
          try {
            const items = data?.segments?.items ?? [];
            const sortedItems = [...items].sort(
              (a, b) => (b?.percentage ?? 0) - (a?.percentage ?? 0)
            );

            const top5 = sortedItems
              .slice(0, 5)
              .map(item => item?.percentage ?? 0);

            if (sortedItems.length > 5) {
              const othersPercentage = sortedItems
                .slice(5)
                .reduce((sum, item) => sum + (item?.percentage ?? 0), 0);
              return [...top5, othersPercentage];
            }

            return top5;
          } catch (error) {
            return [100];
          }
        })(),
        backgroundColor: chartColors.donut,
        borderWidth: 0,
      },
    ],
  };

  return (
    <Paper sx={{ flex: '1 1 300px' }}>
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
          padding: '24px',
          height: '340px',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>
          Tipos de produtos
        </Typography>
        <Box
          sx={{
            position: 'relative',
            height: '280px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Doughnut
            data={donutData}
            width={310}
            height={300}
            options={{
              responsive: false,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    padding: 20,
                    font: {
                      family:
                        "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      size: 14,
                    },
                    color: '#212121',
                    boxWidth: 8,
                    boxHeight: 8,
                  },
                },
              },
              cutout: '70%',
            }}
            plugins={[
              {
                id: 'centerText',
                beforeDraw: chart => {
                  const { ctx, width, chartArea } = chart;
                  ctx.save();
                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';

                  const fontFamily =
                    "'Rubik', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

                  const centerY = (chartArea.top + chartArea.bottom) / 2;

                  const offset = (chartArea.bottom - chartArea.top) * 0.15;

                  ctx.font = `12px ${fontFamily}`;
                  ctx.fillStyle = '#666666';
                  ctx.fillText(
                    'Total investido',
                    width / 2,
                    centerY - offset * 0.3
                  );

                  ctx.font = `bold 16px ${fontFamily}`;
                  ctx.fillStyle = '#000000';
                  ctx.fillText(
                    formatCurrencyToMil(data?.segments?.totalInvestment ?? '0'),
                    width / 2,
                    centerY + offset * 0.3
                  );

                  ctx.restore();
                },
              },
            ]}
          />
        </Box>
      </Box>
    </Paper>
  );
}
